import React, {Suspense, lazy} from "react";
import {Route, Switch, Redirect} from "react-router-dom";
import LoadMessage from "./components/LoadMessage";

// Lazy imports
const Home = lazy(() => import("./containers/Home"));
const Login = lazy(() => import("./containers/Login"));
const Signup = lazy(() => import("./containers/Signup"));
const BusRouteDetails = lazy(() => import("./containers/BusRouteDetails"));
const NotFound = lazy(() => import("./containers/NotFound"));
const AuthenticatedRoute = lazy(() => import("./components/AuthenticatedRoute"));
const UnauthenticatedRoute = lazy(() => import("./components/UnauthenticatedRoute"));
const Schedules = lazy(() => import("./editableList/schedules/Schedules"));
const Overview = lazy(() => import("./containers/Overview"));
const Settings = lazy(() => import("./containers/Settings"));
const ChangePassword = lazy(() => import("./containers/ChangePassword"));
const ChangeEmail = lazy(() => import("./containers/ChangeEmail"));
const Timetable = lazy(() => import("./containers/Timetable"));
const Timetables = lazy(() => import("./containers/Timetables"));
const ResetPassword = lazy(() => import("./containers/ResetPassword"));
const StopManager = lazy(() => import("./containers/StopManager"));
const TripPlanner = lazy(() => import("./containers/TripPlanner"));
const Transfers = lazy(() => import("./containers/Transfers"));
const AutoTransfers = lazy(() => import("./containers/AutoTransfers"));
const NewOperator = lazy(() => import("./components/NewOperator"));
const Students = lazy(() => import("./features/Students/Students"));
const StudentDetails = lazy(() => import("./features/Students/StudentDetails"));
const StudentPasses = lazy(() => import("./features/Students/StudentPasses"));
const StudentPass = lazy(() => import("./features/Students/StudentPass"));
const StudentMisbehaviours = lazy(() => import("./features/Students/StudentMisbehaviours"));
const StudentMisbehaviour = lazy(() => import("./features/Students/StudentMisbehaviour"));
const Operators = lazy(() => import("./editableList/operators/Operators"));
const StopTimetablesList = lazy(() => import("./containers/StopTimetablesList"));
const Deadruns = lazy(() => import("./editableList/deadruns/Deadruns"));
const Fleet = lazy(() => import("./editableList/vehicles/Vehicles"));
const VehicleTypes = lazy(() => import("./editableList/vehicleTypes/VehicleTypes"));
const Drivers = lazy(() => import("./editableList/drivers/Drivers"));
const Shifts = lazy(() => import("./editableList/shifts/Shifts"));
const ShiftBats = lazy(() => import("./features/ShiftBat/ShiftBats"));
const ShiftBatView = lazy(() => import("./features/ShiftBat/ShiftBatView"));
const ShiftBatPrivatePrintView = lazy(() => import("./features/ShiftBat/ShiftBatPrivatePrintView"));
const EmployeeHR = lazy(() => import("./features/EmployeeHR/Elements"));
const Refresh = lazy(() => import("./components/Refresh"));
const Logout = lazy(() => import("./components/Logout"));
const SwitchRegion = lazy(() => import("./components/SwitchRegion"));
const EmployeeManagementDashboard = lazy(() => import("./features/EmployeeHR/EmployeeManagementDashboard"));
const Employees = lazy(() => import("./features/EmployeeHR/Employees"));
const EmployeeDashboard = lazy(() => import("./features/EmployeeHR/EmployeeDashboard"));
const EmployeeDetails = lazy(() => import("./features/EmployeeHR/EmployeeDetails"));
const EmployeeEmployment = lazy(() => import("./features/EmployeeHR/EmployeeEmployment"));
const EmployeeEntitlements = lazy(() => import("./features/EmployeeHR/EmployeeEntitlements"));
const EmployeeBanking = lazy(() => import("./features/EmployeeHR/EmployeeBanking"));
const EmployeeSuperannuation = lazy(() => import("./features/EmployeeHR/EmployeeSuperannuation"));
const EmployeeTax = lazy(() => import("./features/EmployeeHR/EmployeeTax"));
const EmployeeQualificationsAndTraining = lazy(() => import("./features/EmployeeHR/EmployeeQualificationsAndTraining"));
const EmployeeCalendar = lazy(() => import("./features/EmployeeHR/EmployeeCalendar"));
const Marketplace = lazy(() => import("./features/Marketplace/Marketplace"));
const MarketplaceDetails = lazy(() => import("./features/Marketplace/MarketplaceDetails"));
const LiveTrack   = lazy(() => import("./features/Trackable/LiveTrack"));
const FleetTrackingV2 = lazy(() => import("./features/TrackableV2/FleetTrackingV2"));
const WhatsNew = lazy(() => import("./containers/WhatsNew"));
const FirstTimeLoginHelp = lazy(() => import("./containers/FirstTimeLoginHelp"));
const SplashPage = lazy(() => import("./components/SplashPage"));

// Additional lazy imports for remaining components
const ShiftBatViewTemplate = lazy(() => import("./features/ShiftBat/ShiftBatViewTemplate"));
const AdminRoute = lazy(() => import("./components/AdminRoute"));
const SMS = lazy(() => import("./features/Marketplace/SMS"));
const Bookings = lazy(() => import("./features/Bookings/Bookings"));
const BookingDetails = lazy(() => import("./features/Bookings/BookingDetails"));
const BookingDuty = lazy(() => import("./features/Bookings/BookingDuty"));
const CustomerDetails = lazy(() => import("./features/Customers/CustomerDetails"));
const Jobs = lazy(() => import("./editableList/jobs/Jobs"));
const CharterDuty = lazy(() => import('./features/Charters/CharterDuty'));
const Rosters = lazy(() => import("./features/Rostering/Rosters"));
const Blocks = lazy(() => import("./features/Rostering/Blocks"));
const ReportableAdmin = lazy(() => import("./features/Reportable/Admin"));
const ReportableOSR = lazy(() => import("./features/Reportable/OSR"));
const ReportableVisualizer = lazy(() => import("./features/Reportable/Visualizer"));
const ReportableDiff = lazy(() => import("./features/Reportable/DiffChecker"));
const BookingCharter = lazy(() => import("./features/Bookings/BookingCharter"));
const ExpressBooking = lazy(() => import("./features/Bookings/ExpressBooking"));
const ConfirmBooking = lazy(() => import('./features/Bookings/ConfirmBooking'));
const CalendarV2 = lazy(() => import('./features/Calendar/CalendarV2'));
const ImpactAnalysis = lazy(() => import("./containers/ImpactAnalysis"));
const OTRDashboard = lazy(() => import("./features/Reportable/OTRDashboard"));
const Policies = lazy(() => import("./features/EmployeeHR/Policies"));
const ExecutiveDashboard = lazy(() => import("./features/Charters/ExecutiveDashboard"));
const Customers = lazy(() => import("./features/Customers/Customers"));
const EnquriesList = lazy(() => import("./features/Bookings/EnquriesList"));

function RoutesComponent({isAuthenticating, setChatContext, setChatOpen, operator}) {

    return (
        <>
            <Suspense fallback={<div></div>}>
                <Switch>
                    {operator ?
                        <Route exact path="/">
                            <Redirect exact from="/" to={operator?.opts?.external?.optibus?.apiKey ? "/bookings" : "/services"}/>
                        </Route> :
                        <Route exact path="/">
                            <LoadMessage message='Loading Busable...' size={"lg"}/>
                        </Route>
                    }
                    <UnauthenticatedRoute exact path="/login/:id?">
                        <Login/>
                    </UnauthenticatedRoute>
                    <UnauthenticatedRoute exact path="/reset">
                        <ResetPassword/>
                    </UnauthenticatedRoute>
                    <Route exact path="/whats-new">
                        <WhatsNew/>
                    </Route>
                    <Route exact path="/first-time-login-help">
                        <FirstTimeLoginHelp/>
                    </Route>
                    <Route path="/confirm-booking">
                        <ConfirmBooking/>
                    </Route>
                    {/*<UnauthenticatedRoute exact path="/timetable">*/}
                    {/*    <TimetableOverview />*/}
                    {/*</UnauthenticatedRoute>*/}
                    <Route exact path="/timetables/:key?/timetable/:id">
                        <Timetable/>
                    </Route>
                    <Route exact path="/timetables/:key?">
                        <Timetables setChatContext={setChatContext} setChatOpen={setChatOpen}/>
                    </Route>
                    <Route exact path="/trip_planner/:key?">
                        <TripPlanner/>
                    </Route>
                    <Route exact path="/school_list/:key?">
                        <StopTimetablesList stopType={'school'}/>
                    </Route>
                    <Route exact path="/venue_list/:key?">
                        <StopTimetablesList stopType={'venue'}/>
                    </Route>
                    <Route exact path="/charter_booking/:key?">
                        <ExpressBooking isEmbedded={true}/>
                    </Route>
                    <Route exact path="/p_sb/:key/:id">
                        <ShiftBatPrivatePrintView/>
                    </Route>
                    <AuthenticatedRoute exact path="/splash">
                        <SplashPage operator={operator} />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/marketplace" viewPath="/marketplace">
                        <Marketplace/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/marketplace/:id" viewPath="/marketplace">
                        <MarketplaceDetails/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/sms" viewPath="/sms">
                        <SMS/>
                    </AuthenticatedRoute>
                    <UnauthenticatedRoute exact path="/trial">
                        <Signup/>
                    </UnauthenticatedRoute>
                    <AuthenticatedRoute exact path="/refresh">
                        <Refresh/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/settings" viewPath="/settings">
                        <Settings/>
                    </AuthenticatedRoute>
                    <UnauthenticatedRoute exact path="/logout">
                        <Logout/>
                    </UnauthenticatedRoute>
                    <AuthenticatedRoute exact path="/switch/:companyId/:operatorId">
                        <SwitchRegion/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/settings/password">
                        <ChangePassword/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/settings/email">
                        <ChangeEmail/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/points/:stopId?">
                        <StopManager/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/services" viewPath="/services">
                        <Home/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/transfers" viewPath="/transfers">
                        <Transfers/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/autoTransfers">
                        <AutoTransfers/>
                    </AuthenticatedRoute>
                    {/*<AuthenticatedRoute exact path="/services/#new">*/}
                    {/*    <NewBusRoute/>*/}
                    {/*</UnauthenticatedRoute>*/}
                    <AuthenticatedRoute exact path="/services/:id">
                        <BusRouteDetails/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/calendars">
                        <Schedules/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/calendars-v2">
                        <CalendarV2/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/students">
                        <Students/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/students/:studentId">
                        <StudentDetails/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/students/:studentId/passes">
                        <StudentPasses/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/students/:studentId/passes/:passId">
                        <StudentPass/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/students/:studentId/misbehaviours">
                        <StudentMisbehaviours/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/students/:studentId/misbehaviours/:incidentId">
                        <StudentMisbehaviour/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/overview">
                        <Overview/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/new_operator">
                        <NewOperator/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/fleet">
                        <Fleet/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/vehicleTypes">
                        <VehicleTypes />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/drivers">
                        <Drivers/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/shifts">
                        <Shifts/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/shiftbats">
                        <ShiftBats/>
                    </AuthenticatedRoute>
                    <AdminRoute exact path="/operators">
                        <Operators/>
                    </AdminRoute>
                    <AuthenticatedRoute exact path="/shiftbatsTemplate">
                        <ShiftBatViewTemplate/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/shiftbats/:id">
                        <ShiftBatView/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm">
                        <EmployeeManagementDashboard/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/all">
                        <Employees/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/dashboard">
                        <EmployeeDashboard/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/details">
                        <EmployeeDetails/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/employment">
                        <EmployeeEmployment/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/entitlements">
                        <EmployeeEntitlements/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/banking">
                        <EmployeeBanking/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/superannuation">
                        <EmployeeSuperannuation/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/tax">
                        <EmployeeTax/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/qualifications">
                        <EmployeeQualificationsAndTraining/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/:id/calendar">
                        <EmployeeCalendar/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/hrm/elements">
                        <EmployeeHR/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/fleet_monitoring">
                        <LiveTrack/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/fleet_monitoring_v2">
                        <FleetTrackingV2/>
                    </AuthenticatedRoute>
                    {/* Redirect to bookings for all charters routes */}
                    <AuthenticatedRoute exact path="/charters/executive-dashboard" viewPath="/charters/executive-dashboard">
                         <Redirect exact from="/charters/executive-dashboard" to="/bookings/executive-dashboard" />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute path="/charters" viewPath="/charters">
                        <Redirect exact from="/charters" to="/bookings" />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/bookings/executive-dashboard" viewPath="/bookings/executive-dashboard">
                        <ExecutiveDashboard />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/enquiries" viewPath="/enquiries">
                        <EnquriesList />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/bookings" viewPath="/bookings">
                        <Bookings />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/bookings/:charterId">
                        <BookingDetails />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/bookings/:charterId/charter/:routeId">
                        <BookingCharter />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/bookings/:charterId/duties/:dutyId">
                        <BookingDuty />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/customers">
                        <Customers />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/customers/:customerId">
                        <CustomerDetails />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/rostering">
                        <Rosters />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/blocks">
                        <Blocks/>
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/reportable/admin" viewPath="/reportable/admin">
                        <ReportableAdmin />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/reportable/osr" viewPath="/reportable/osr">
                        <ReportableOSR />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/reportable/visualizer" viewPath="/reportable/visualizer">
                        <ReportableVisualizer />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/reportable/diff" viewPath="/reportable/diff">
                        <ReportableDiff />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/reportable/otr-dashboard" viewPath="/reportable/otr-dashboard">
                        <OTRDashboard />
                    </AuthenticatedRoute>

                    <AuthenticatedRoute exact path="/impact-analysis">
                        <ImpactAnalysis />
                    </AuthenticatedRoute>

                    <AuthenticatedRoute exact path="/jobs">
                        <Jobs />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/policies" >
                        <Policies />
                    </AuthenticatedRoute>
                    <AuthenticatedRoute exact path="/deadruns">
                        <Deadruns />
                    </AuthenticatedRoute>
                    {/* Finally, catch all unmatched routes */}
                    <Route>
                        <NotFound/>
                    </Route>
                </Switch>
            </Suspense>
        </>
    );
}

export default React.memo(RoutesComponent)
